import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Paper
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import LayersIcon from '@mui/icons-material/Layers';
import RoomIcon from '@mui/icons-material/Room';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DevicesIcon from '@mui/icons-material/Devices';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import UnitCreateDialog from './UnitCreateDialog';
import UnitApplianceDialog from './UnitApplianceDialog';

interface City {
  id: string;
  name: string;
  active: boolean;
}

interface Country {
  id: string;
  name: string;
  isoCode: string;
  active: boolean;
}

interface Project {
  id: string;
  name: string;
  number: string;
  street: string;
  active: boolean;
}

interface Property {
  id: string;
  code: string;
  street: string;
  floor: number;
  contact: string;
  countryId: string;
  cityId: string;
  projectId: string;
  propertyTypeId: string;
  propertyType: string;
  city: City | null;
  country: Country | null;
  project: Project | null;
  units: any[];
  active: boolean;
}

interface PropertyDetailsDialogProps {
  open: boolean;
  property: Property | null;
  onClose: () => void;
  onRefresh?: () => void;
}

const PropertyDetailsDialog: React.FC<PropertyDetailsDialogProps> = ({
  open,
  property,
  onClose,
  onRefresh
}) => {
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [applianceDialogOpen, setApplianceDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<{ id: string; number: string } | null>(null);
  const dispatch = useAppDispatch();

  // Handle unit creation success
  const handleUnitSuccess = () => {
    setUnitDialogOpen(false);
    // Call onRefresh to reload the properties list with updated units
    if (onRefresh) {
      onRefresh();
    }
    dispatch(showAlert({
      message: 'Unit created successfully! Refresh the list to see the new unit.',
      type: 'success'
    }));
  };

  // Format currency helper
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Get non-zero fee periods
  const getNonZeroFees = (feeConfig: any) => {
    const periods = [
      { label: 'Daily', value: feeConfig.daily },
      { label: 'Weekly', value: feeConfig.weekly },
      { label: 'Monthly', value: feeConfig.monthly },
      { label: 'Bimonthly', value: feeConfig.bimonthly },
      { label: 'Quarterly', value: feeConfig.quarterly },
      { label: 'Semi-Annual', value: feeConfig.semiAnnual },
      { label: 'Annual', value: feeConfig.annual },
      { label: 'One Time', value: feeConfig.oneTime }
    ];
    return periods.filter(period => period.value > 0);
  };

  if (!property) return null;

  // Generate initials from property code
  const initials = property?.code?.substring(0, 2).toUpperCase() || 'PR';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
              fontSize: '1rem'
            }}
          >
            {initials || <HomeIcon />}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
              {property.code}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0 }}>
              <Chip
                icon={property.active ? <CheckCircleIcon /> : <CancelIcon />}
                label={property.active ? 'Active' : 'Inactive'}
                color={property.active ? 'success' : 'default'}
                size="small"
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                ID: {property.id}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }}>
        <Grid container spacing={1.5}>
            {/* Property Information */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', mb: 1 }}>
                    <HomeIcon color="primary" fontSize="small" />
                    Property Information
                  </Typography>

                  {/* Address */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Address
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {property.street}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Floor {property.floor}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Location */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Location
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PublicIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {property.city?.name}, {property.country?.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Country Code: {property.country?.isoCode}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Contact */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Contact Person
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
                      <ContactPhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {property.contact || (
                          <span style={{ fontStyle: 'italic', color: 'gray' }}>
                            No contact specified
                          </span>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Project Information */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', mb: 1 }}>
                    <BusinessIcon color="primary" fontSize="small" />
                    Project Information
                  </Typography>

                  {/* Project Details */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Project
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {property.project?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Project #{property.project?.number}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Project Address */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Project Address
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {property.project?.street}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Property Type */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Property Type
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Chip
                        label={property.propertyType}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Units */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                      <LayersIcon color="primary" fontSize="small" />
                      Units ({property.units?.length || 0})
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setUnitDialogOpen(true)}
                      size="small"
                    >
                      Add Unit
                    </Button>
                  </Box>

                  {property.units && property.units.length > 0 ? (
                    <Grid container spacing={1}>
                      {property.units.map((unit, index) => (
                        <Grid item xs={12} sm={6} md={4} key={unit.id || index}>
                          <Card
                            variant="outlined"
                            sx={{
                              height: '100%',
                              border: '1px solid',
                              borderColor: unit.active ? 'success.main' : 'grey.300',
                              backgroundColor: unit.active ? 'success.50' : 'grey.50'
                            }}
                          >
                            <CardContent sx={{ p: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  {unit.number}
                                </Typography>
                                <Chip
                                  label={unit.active ? 'Active' : 'Inactive'}
                                  size="small"
                                  color={unit.active ? 'success' : 'default'}
                                  variant="outlined"
                                />
                              </Box>

                              <List dense>
                                <ListItem sx={{ px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <LayersIcon fontSize="small" color="action" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={`Floor ${unit.floor}`}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                  />
                                </ListItem>

                                <ListItem sx={{ px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <RoomIcon fontSize="small" color="action" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={`${unit.rooms} Room${unit.rooms !== 1 ? 's' : ''}`}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                  />
                                </ListItem>

                                <ListItem sx={{ px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <BathtubIcon fontSize="small" color="action" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={`${unit.bathrooms} Bathroom${unit.bathrooms !== 1 ? 's' : ''}`}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                  />
                                </ListItem>

                                <ListItem sx={{ px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <SquareFootIcon fontSize="small" color="action" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={`${unit.areaM2} m²`}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                  />
                                </ListItem>
                              </List>

                              {/* Fee Configurations */}
                              {unit.unitFeeConfigs && unit.unitFeeConfigs.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Divider sx={{ mb: 1 }} />
                                  <Accordion
                                    elevation={0}
                                    sx={{
                                      bgcolor: 'transparent',
                                      '&:before': { display: 'none' }
                                    }}
                                  >
                                    <AccordionSummary
                                      expandIcon={<ExpandMoreIcon />}
                                      sx={{
                                        px: 0,
                                        minHeight: 40,
                                        '&.Mui-expanded': { minHeight: 40 }
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AttachMoneyIcon fontSize="small" color="success" />
                                        <Typography variant="subtitle2" fontWeight="bold">
                                          Fee Configurations ({unit.unitFeeConfigs.length})
                                        </Typography>
                                      </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                                      <Stack spacing={1}>
                                        {unit.unitFeeConfigs.map((feeConfig: any, idx: number) => {
                                          const nonZeroFees = getNonZeroFees(feeConfig);
                                          if (nonZeroFees.length === 0) return null;

                                          return (
                                            <Paper
                                              key={feeConfig.id || idx}
                                              elevation={0}
                                              sx={{
                                                p: 1,
                                                bgcolor: 'primary.50',
                                                border: '1px solid',
                                                borderColor: 'primary.200',
                                                borderRadius: 1.5,
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                  borderColor: 'primary.main',
                                                  boxShadow: 1
                                                }
                                              }}
                                            >
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <AttachMoneyIcon
                                                  fontSize="small"
                                                  sx={{ color: 'success.main' }}
                                                />
                                                <Typography
                                                  variant="subtitle2"
                                                  fontWeight="bold"
                                                  color="primary.main"
                                                >
                                                  {feeConfig.feeType?.name || 'Fee'}
                                                </Typography>
                                              </Box>

                                              {feeConfig.feeType?.description && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}
                                                >
                                                  {feeConfig.feeType.description}
                                                </Typography>
                                              )}

                                              <Grid container spacing={1}>
                                                {nonZeroFees.map((period, pIdx) => (
                                                  <Grid item xs={6} key={pIdx}>
                                                    <Box
                                                      sx={{
                                                        bgcolor: 'white',
                                                        p: 1,
                                                        borderRadius: 1,
                                                        border: '1px solid',
                                                        borderColor: 'divider'
                                                      }}
                                                    >
                                                      <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: 'block', fontSize: '0.7rem' }}
                                                      >
                                                        {period.label}
                                                      </Typography>
                                                      <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        color="success.dark"
                                                      >
                                                        {formatCurrency(period.value)}
                                                      </Typography>
                                                    </Box>
                                                  </Grid>
                                                ))}
                                              </Grid>
                                            </Paper>
                                          );
                                        })}
                                      </Stack>
                                    </AccordionDetails>
                                  </Accordion>
                                </Box>
                              )}

                              {unit.address && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Address: {unit.address}
                                  </Typography>
                                </Box>
                              )}

                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Unit ID: {unit.id}
                              </Typography>

                              {/* Manage Appliances Button */}
                              <Box sx={{ mt: 1 }}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  startIcon={<DevicesIcon />}
                                  onClick={() => {
                                    setSelectedUnit({ id: unit.id, number: unit.number });
                                    setApplianceDialogOpen(true);
                                  }}
                                >
                                  Manage Appliances
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <LayersIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        No units found for this property
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </DialogActions>

      {/* Unit Create Dialog */}
      {property && (
        <UnitCreateDialog
          open={unitDialogOpen}
          propertyId={property.id}
          onClose={() => setUnitDialogOpen(false)}
          onSuccess={handleUnitSuccess}
        />
      )}

      {/* Unit Appliance Dialog */}
      {selectedUnit && (
        <UnitApplianceDialog
          open={applianceDialogOpen}
          onClose={() => {
            setApplianceDialogOpen(false);
            setSelectedUnit(null);
          }}
          unitId={selectedUnit.id}
          unitNumber={selectedUnit.number}
        />
      )}
    </Dialog>
  );
};

export default PropertyDetailsDialog;